import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Typography,
  Chip,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { Delete, PersonAdd, Visibility } from '@mui/icons-material';
import type { Notebook } from '@/types/notebook';
import { useInviteToNotebook, useRemoveNotebookSharedUser } from '@/hooks';
import { brandColors } from '@/theme/colors';

type NotebookShareDialogProps = {
  open: boolean;
  onClose: () => void;
  notebook: Notebook;
};

export const NotebookShareDialog = ({ open, onClose, notebook }: NotebookShareDialogProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const inviteMutation = useInviteToNotebook();
  const removeMutation = useRemoveNotebookSharedUser();

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const MAX_SHARED_USERS = 20;

  const handleInvite = async () => {
    setError('');

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (email.toLowerCase() === notebook.ownerEmail.toLowerCase()) {
      setError('You cannot invite yourself');
      return;
    }

    if (notebook.sharedWith.some((s) => s.email.toLowerCase() === email.toLowerCase())) {
      setError('This user has already been invited');
      return;
    }

    if (notebook.sharedWith.length >= MAX_SHARED_USERS) {
      setError(`This notebook has reached the maximum of ${MAX_SHARED_USERS} shared users`);
      return;
    }

    try {
      await inviteMutation.mutateAsync({
        notebookId: notebook.id,
        input: { email: email.toLowerCase() },
      });
      setEmail('');
    } catch (err) {
      setError('Failed to send invitation');
    }
  };

  const handleRemove = async (userEmail: string) => {
    try {
      await removeMutation.mutateAsync({
        notebookId: notebook.id,
        userEmail,
      });
    } catch (err) {
      console.error('Failed to remove user:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: brandColors.purple }}>
        Share "{notebook.name}"
      </DialogTitle>
      <DialogContent>
        {/* View-only notice */}
        <Alert severity="info" sx={{ mb: 2, mt: 1 }} icon={<Visibility />}>
          Shared users can <strong>view only</strong>. They cannot edit notebook content.
        </Alert>

        {/* Invite Form */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            placeholder="colleague@example.com"
          />
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviteMutation.isPending}
            startIcon={<PersonAdd />}
            sx={{
              bgcolor: brandColors.purple,
              '&:hover': { bgcolor: brandColors.purpleDeep },
            }}
          >
            Invite
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Owner */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Owner
        </Typography>
        <ListItem sx={{ px: 0 }}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: brandColors.purple }}>
              {notebook.ownerName?.[0] || notebook.ownerEmail[0]}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={notebook.ownerName || notebook.ownerEmail}
            secondary={notebook.ownerEmail}
          />
        </ListItem>

        {/* Shared Users */}
        {notebook.sharedWith.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Shared with ({notebook.sharedWith.length})
            </Typography>
            <List dense disablePadding>
              {notebook.sharedWith.map((share) => (
                <ListItem key={share.id} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar src={share.photoURL || undefined}>
                      {share.displayName?.[0] || share.email[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {share.displayName || share.email}
                        <Chip
                          label={share.status}
                          size="small"
                          color={getStatusColor(share.status) as 'warning' | 'success' | 'error' | 'default'}
                          variant="outlined"
                        />
                        <Chip
                          label="View only"
                          size="small"
                          variant="outlined"
                          icon={<Visibility sx={{ fontSize: 14 }} />}
                        />
                      </Box>
                    }
                    secondary={share.displayName ? share.email : undefined}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(share.email)}
                      disabled={removeMutation.isPending}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {notebook.sharedWith.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Not shared with anyone yet
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};
